<script setup lang="ts">
const { t: $t } = useI18n()

import type { FormKitNode } from '@formkit/core'
import { OfferCancellationReason } from '~/prisma/enums'

const props = defineProps<{
  payload: {
    id: string
    isCancelled: boolean
  }
  view: 'offer' | 'order' | 'customerModule'
}>()

const emit = defineEmits(['close', 'confirm'])

interface FormPayload {
  cancellationReason: OfferCancellationReason
  otherReason: string | null
}

const cancellationReason = ref<OfferCancellationReason | undefined>(
  props.view === 'customerModule'
    ? undefined
    : OfferCancellationReason.DeliveryCostTooHigh,
)
const otherReason = ref<string | null>(null)

const cancellationReasonOptions = Object.entries(OfferCancellationReason).map(([value]) => {
  if (value === OfferCancellationReason.Other) {
    return {
      label: `${$t(`offer.rental.cancellationReason.option.${value}`)} (${$t('offer.rental.cancellationReason.popup.otherReason.extraText')})`,
      value,
    }
  }
  if (value === OfferCancellationReason.CustomerNotInterested && props.view === 'customerModule') {
    return {
      label: $t(`offer.rental.cancellationReason.option.CustomerModule.${value}`),
      value,
    }
  }

  return { label: $t(`offer.rental.cancellationReason.option.${value}`), value }
})

const checkboxes = ref<null | FormKitNode>(null)
function reset() {
  const nodeValue = checkboxes.value
  if (!nodeValue) {
    return
  }
  // @ts-expect-error Unsure why this is undefined, but it is. Check: https://formkit.com/essentials/architecture#using-a-template-ref
  const actualNode = nodeValue.node
  actualNode.input(null)
}

function submit(payload: FormPayload) {
  emit('confirm', {
    id: props.payload.id,
    isCancelled: props.payload.isCancelled,
    cancellationReason: payload.cancellationReason ?? null,
    otherCancellationReason: payload.otherReason,
  })
}
function closePopup() {
  emit('close')
}
</script>

<template>
  <ThePopup
    show
    :title="$t('global.confirm.title')"
    @close="closePopup"
  >
    <FormKit type="form" :actions="false" @submit="submit">
      <div class="mb-5">
        {{ $t(`offer.rental.cancellationReason.${view}.popup.description`) }}
      </div>

      <FormKit
        ref="checkboxes"
        v-model="cancellationReason"
        name="cancellationReason"
        type="radio"
        :validation="view === 'customerModule' ? undefined : 'required'"
        :options="cancellationReasonOptions"
        :classes="{
          fieldset: 'border-none',
        }"
      />
      <!-- @vue-expect-error: After upgrading to latest Vue 3.5 and TS 5.8 in #4136 inference became stricter, generating a new error for a working form, that had no explicit error before. To enable the upgrade we add this ignore: The TS-error was there before, it was just not caught. Now, its at least explicit and can be fixed in the future. TODO: Fix, improve or replace once we agree on a systematic fix -->
      <FormKit
        v-if="cancellationReason === OfferCancellationReason.Other"
        v-model="otherReason"
        name="otherReason"
        type="text"
        validation="required"
        :placeholder="$t('offer.rental.cancellationReason.option.text.placeholder')"
        :validation-messages="{ required: $t('offer.rental.cancellationReason.popup.otherReason.error.message') }"
      />

      <div class="flex items-center justify-end gap-2 mt-4">
        <n-button v-if="view === 'customerModule'" size="large" :bordered="false" :circle="true" @click="reset">
          <template #icon>
            <Icon name="material-symbols:device-reset-rounded" size="36px" />
          </template>
        </n-button>

        <n-button @click="$emit('close')">
          {{ $t('button.cancel') }}
        </n-button>
        <n-button type="primary" attr-type="submit">
          {{ view === 'customerModule' ? $t('button.confirm') : $t('button.save') }}
        </n-button>
      </div>
    </FormKit>
  </ThePopup>
</template>
